import { Check, Copy, Link as LinkIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { getIcon } from "../shared/renderer/utils/iconMap";
import type { IntegrationUrlsProps, MetricCardsProps, UiComponent } from "../types";

import { Button } from "@/components/ui/v2/button";
import { Card, CardContent } from "@/components/ui/v2/card";
import { Separator } from "@/components/ui/v2/separator";
import MetricCard from "@/features/campaigns/jobboards/components/MetricCard";
import { useClient } from "@/features/clients/api/useClient";
import { useClientJobBoards } from "@/features/clients/jobboards/api/useClientJobBoards";
import { baseURL } from "@/lib/axios/instance";
import { normalizeName } from "@/lib/utils/stringUtils";
import type { ClientJobBoardType } from "@/types/jobboards/ClientJobBoardType";

interface IntegrationInfoSectionProps {
  integrationName: string;
  clientId?: string;
  configurationSchemaJson?: string;
}

function IntegrationUrlCard({ label, url }: { label: string; url: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (copied) return;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="block no-underline">
      <Card hover padding="xs">
        <CardContent className="flex items-center gap-4 px-5 py-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted/60 border border-foreground/10">
            <LinkIcon className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="min-w-0">
            <span className="text-xs text-muted-foreground mb-0.5 block">
              {label}
            </span>
            <span className="text-sm font-semibold text-primary truncate block">
              {url}
            </span>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={handleCopy}
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-green-600" />
            ) : (
              <Copy className="h-3.5 w-3.5 text-muted-foreground" />
            )}
          </Button>
        </CardContent>
      </Card>
    </a>
  );
}

function findUiComponentProps<T>(schemaJson: string | undefined, component: string): T | null {
  if (!schemaJson) return null;
  try {
    const ui: UiComponent[] = (JSON.parse(schemaJson).ui ?? []);
    return (ui.find((c) => c.component === component)?.props as T) ?? null;
  } catch {
    return null;
  }
}

function buildFeedUrl(jobBoard: ClientJobBoardType): string {
  return `${baseURL}/api/v2/JobBoards/${jobBoard.jobBoard.id}/Clients/${jobBoard.clientId2}`;
}

function buildPostUrl(jobBoard: ClientJobBoardType, clientName: string | undefined): string {
  const identifier = jobBoard.jobBoard.applyByClientId
    ? jobBoard.clientId2
    : encodeURIComponent(clientName ?? "");
  return `${baseURL}/api/v2/Webhook/receive/${jobBoard.jobBoard.shortName ?? jobBoard.jobBoard.name}/${identifier}`;
}

function resolveUrl(key: string, matchingJobBoard: ClientJobBoardType, clientName: string | undefined): string | null {
  switch (key) {
  case "feed_url":
    return buildFeedUrl(matchingJobBoard);
  case "post_url":
    return buildPostUrl(matchingJobBoard, clientName);
  default:
    return null;
  }
}

function IntegrationInfoSection({ integrationName, clientId, configurationSchemaJson }: IntegrationInfoSectionProps) {
  const { t } = useTranslation();

  const cards = useMemo(
    () => findUiComponentProps<MetricCardsProps>(configurationSchemaJson, "metricCards")?.cards ?? [],
    [configurationSchemaJson],
  );

  const urlDefinitions = useMemo(
    () => findUiComponentProps<IntegrationUrlsProps>(configurationSchemaJson, "integrationUrls")?.urls ?? [],
    [configurationSchemaJson],
  );

  const showUrls = urlDefinitions.length > 0;

  const { data: client } = useClient(showUrls ? clientId : undefined);

  const { data: clientJobBoards } = useClientJobBoards({
    clientId,
    enabled: Boolean(clientId) && showUrls,
  });

  const normalizedIntegrationName = normalizeName(integrationName);
  const matchingJobBoard = showUrls
    ? clientJobBoards?.data?.find(
      (jb) => normalizeName(jb.jobBoard.name) === normalizedIntegrationName
    )
    : undefined;

  const resolvedUrls = matchingJobBoard
    ? urlDefinitions
      .map((def) => ({ label: t(def.label), url: resolveUrl(def.key, matchingJobBoard, client?.name) }))
      .filter((entry): entry is { label: string; url: string } => entry.url !== null)
    : [];

  const hasContent = cards.length > 0 || resolvedUrls.length > 0;

  if (!hasContent) return null;

  return (
    <div className="flex flex-col gap-6">
      {cards.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-3">
          {cards.map((card) => {
            const value = t(card.dataKey);
            const Icon = getIcon(card.icon);
            const iconElement = Icon ? <Icon className="size-5 text-primary" /> : null;
            const subtitle = card.subtitleDataKey
              ? t(card.subtitleDataKey)
              : card.subtitle
                ? t(card.subtitle)
                : undefined;

            return (
              <MetricCard
                key={card.dataKey}
                variant="gradient"
                icon={iconElement}
                label={t(card.label)}
                value={value}
                subtitle={subtitle}
              />
            );
          })}
        </div>
      )}

      {resolvedUrls.length > 0 && (
        <div className="flex flex-col gap-3">
          <h3 className="text-sm font-semibold text-foreground">
            {t("integrations.info.integrationUrls")}
          </h3>
          <div className="grid gap-4 sm:grid-cols-3">
            {resolvedUrls.map((entry) => (
              <IntegrationUrlCard
                key={entry.url}
                label={entry.label}
                url={entry.url}
              />
            ))}
          </div>
        </div>
      )}

      <Separator />
    </div>
  );
}

export default IntegrationInfoSection;
